import styled from '@emotion/styled';

import { Accordion } from '@/components/common/Accordion/Accordion';
import { OuterWrapper } from '@/components/common/Accordion/AccordionItemConfig';
import { RichHeading } from '@/components/common/Heading';
import { HeadingLevel } from '@/components/common/MarkUp';
import { headingSmall, labelMedium } from '@/styles/typography';

import type { DefaultAccordionItemProps } from '@/components/common/Accordion/DefaultAccordionItem';
import type { BlockWithAudience, Heading } from '@/types/shared';

export interface NestedAccordionProps extends BlockWithAudience {
  heading?: Heading;
  outerAccordion?: {
    _key: string;
    title?: string;
    accordion: DefaultAccordionItemProps[];
  }[];
}

export const NestedAccordion = ({
  outerAccordion,
  heading,
  pageAudience,
  isAudienceSwitcherEnabled,
}: NestedAccordionProps) => {
  // Build a new accordion and insert isExpandable if there's no body of nested accordion items
  const expandableOuterAccordion = outerAccordion?.map((outerAccordionItem) => {
    const newOuterAccordionItem = { ...outerAccordionItem };

    // Step into the outer layer of accordion items
    newOuterAccordionItem.accordion = newOuterAccordionItem.accordion.map(
      (innerAccordionItem) => {
        const newInnerAccordionItem = { ...innerAccordionItem };

        // Step into the inner (nested) layer of accordion items
        newInnerAccordionItem.accordion = newInnerAccordionItem.accordion.map(
          (item) => {
            const newItem = { ...item };
            if (!newItem.body) {
              newItem.isExpandable = false;
            }
            return newItem;
          },
        );
        return newInnerAccordionItem;
      },
    );
    return newOuterAccordionItem;
  });

  return (
    <HeadingLevel>
      {heading && (
        <HeadingWrapper>
          <RichHeading heading={heading} size="heading-md" />
        </HeadingWrapper>
      )}
      <AccordionWrapper>
        {expandableOuterAccordion?.map((item, idx) => {
          return (
            <div key={item._key}>
              {item?.title && (
                <AccordionItemTitle>{item.title}</AccordionItemTitle>
              )}
              <StyledAccordion
                accordionBehaviour="allItems"
                accordionItems={item.accordion}
                pageAudience={pageAudience}
                firstItemInAllItemsOpened
                isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
                chevronSize="12px"
                className={`inner-accordion_${idx}`}
                startingIndex={expandableOuterAccordion
                  .slice(0, idx)
                  .reduce(
                    (acc, innerItem) => innerItem.accordion.length + acc,
                    0,
                  )}
                headingPadding={{
                  openState: {
                    paddingLeft: 'var(--space-component-xl)',
                    paddingRight: 'var(--space-component-xl)',
                    paddingTop: 'var(--space-component-xl)',
                  },
                  closedState: {
                    paddingLeft: 'var(--space-component-xl)',
                    paddingRight: 'var(--space-component-xl)',
                    paddingTop: 'var(--space-component-xl)',
                  },
                }}
              />
            </div>
          );
        })}
      </AccordionWrapper>
    </HeadingLevel>
  );
};

const HeadingWrapper = styled.div`
  padding-bottom: var(--space-component-xxl);
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: var(--font-weight-semibold);
  }
`;

const StyledAccordion = styled(Accordion)`
  :not(:last-child) {
    padding-bottom: var(--space-component-xxl);
  }

  > li {
    border-radius: var(--radius-component-xl);
    border: 1px solid var(--border-warm-base);

    h3,
    h4,
    h5,
    h6 {
      ${headingSmall}
    }
    p:not(:last-of-type) {
      padding-bottom: var(--space-component-md);
    }
    > ${OuterWrapper} {
      padding-bottom: var(--space-component-xl);
      > div {
        padding-left: var(--space-component-xl);
        padding-right: var(--space-component-xl);
      }
    }
  }

  > li:not(:first-child) {
    margin-top: var(--space-component-lg);
  }
`;

const AccordionItemTitle = styled.span`
  display: block;
  ${labelMedium}
  color: var(--text-warm-subtle);
  padding-bottom: var(--space-component-lg);
`;

const AccordionWrapper = styled.div`
  > div :not(:last-child) {
    margin-bottom: var(--space-component-xxl);
  }
`;
